import { useState, useCallback, useEffect } from 'react';
import api from '../services/api'; // Adjust path if api.js is elsewhere

// Note: clearAutoForwardTimer and socket are not directly managed by this hook,
// they will be called from within ConversationView or another dedicated hook for side effects.
// This hook focuses on fetching and managing conversation list and selection.

export const useConversationState = (clientId, token, initialSelectedConvo = null) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(initialSelectedConvo);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchConversations = useCallback(async () => {
    if (!clientId || !token) {
        setError("Client ID or token not provided for fetching conversations.");
        setLoading(false);
        return;
    }
    setLoading(true);
    setError('');
    // setSelectedConversation(null); // Resetting selected here might be too aggressive if called for a refresh

    try {
      const response = await api.getClientConversations(clientId, token);
      // Handle both array response and object with conversations property
      const conversationsArray = Array.isArray(response) ? response : (response?.conversations || []);
      console.log('[useConversationState] Fetched conversations:', {
        responseType: Array.isArray(response) ? 'array' : 'object',
        count: conversationsArray.length
      });
      setConversations(conversationsArray);
    } catch (err) {
      console.error("Error fetching client conversations in hook:", err);
      setError(err.response?.data?.msg || err.message || 'Failed to fetch conversations.');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]); // Re-fetch if clientId or token changes

  // This simplified handleSelectConversation will be used by ConversationView
  // The more complex state resetting logic will remain in ConversationView's version
  // or be moved to a more specialized effect/hook later.
  // ST-20260922-0002: exposed as `setSelectedConversation` and named in
  // ConversationView's effect dependencies. As a bare function literal it took
  // a new identity on every render, so that effect re-ran every render. It is
  // self-limiting on its own, but it re-arms continuously under any re-render
  // loop above it, so it is memoised here rather than left to the caller.
  const selectConversation = useCallback((conversation) => {
    setSelectedConversation(conversation);
  }, []);

  return {
    conversations,
    selectedConversation,
    loading,
    error,
    fetchConversations, // Expose for manual refresh if needed
    setSelectedConversation: selectConversation, // Expose a setter
    setConversationsError: setError, // Allow parent to set error if needed
    setConversationsLoading: setLoading, // Allow parent to set loading if needed
  };
};
